import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import DatePicker from 'react-datepicker';
import { FormattedMessage, useIntl } from 'react-intl';

import { getLocale } from 'src/util/i18n/handler';
import { dateToTimeInTimezone } from 'src/util/time';

import buildLocalizedDateRangeLabel from 'src/util/time/dateRange';
import TimeSpanButton from '../TimeSpanButton';

/**
 * Wrap a DatePicker to support a custom timezone.
 * @param {object} props
 * @param {string} props.timezone
 * @param {DateTime} props.start
 * @param {DateTime} props.finish
 * @param {number} props.maxNumberDays
 * @param {number} props.minStart
 * @param {Function} props.onChange
 * @param {Function} props.onCalendarClose
 * @param {Function} props.onCalendarOpen
 * @param {Function} props.customCalendarClose
 * @param {boolean} props.isModalOpen
 * @returns {DatePickerInTimezone} -  React date picker component wrapped in a time zone(preferred)
 */
const DatePickerInTimezone = React.forwardRef((
  {
    timezone,
    start,
    finish,
    minStart,
    maxNumberDays,
    onChange,
    onCalendarClose,
    onCalendarOpen,
    customCalendarClose,
    isModalOpen,
  },
  ref,
) => {
  const intl = useIntl();
  const dpStart = start ? DateTime.local(start.year, start.month, start.day).toJSDate() : null;
  const dpFinish = finish ? DateTime.local(finish.year, finish.month, finish.day).toJSDate() : null;
  const dpMaxDate = dpStart && !dpFinish
    ? DateTime.fromJSDate(dpStart).plus({ days: maxNumberDays }).toJSDate() : null;
  const dpMinDate = minStart && Number(minStart) ? DateTime.fromSeconds(minStart).toJSDate() : null;
  const label = buildLocalizedDateRangeLabel(intl, start, finish, timezone);

  const wrappedOnChange = (dates) => {
    const [localStartJS, localFinishJS] = dates;
    const localStart = localStartJS ? DateTime.fromJSDate(localStartJS) : null;
    const localFinish = localFinishJS ? DateTime.fromJSDate(localFinishJS) : null;

    const preferredStart = localStart ? dateToTimeInTimezone(localStart, timezone) : null;
    const preferredFinish = localFinish ? dateToTimeInTimezone(localFinish, timezone) : null;

    onChange([preferredStart, preferredFinish]);
  };

  return (
    <DatePicker
      autoFocus={false}
      locale={getLocale()}
      ref={ref}
      value={label}
      selected={dpStart && !dpFinish ? dpStart : null}
      startDate={dpStart || null}
      endDate={dpFinish || null}
      maxDate={dpMaxDate}
      minDate={dpMinDate}
      onChange={wrappedOnChange}
      onCalendarClose={onCalendarClose}
      onCalendarOpen={onCalendarOpen}
      customInput={<TimeSpanButton isModalOpen={isModalOpen} />}
      monthsShown={2}
      selectsRange
      shouldCloseOnSelect={false}
      popperPlacement="bottom-end"
      popperModifiers={{
        preventOverflow: {
          enabled: true,
          escapeWithReference: false,
          boundariesElement: 'viewport',
        },
      }}
    >
      <div
        style={{
          clear: 'both',
          textAlign: 'right',
          borderTop: '1px solid #ccc',
          padding: '1em',
        }}
      >
        <button className="btn btn-primary" type="button" onClick={customCalendarClose}>
          <FormattedMessage id="common.daterange_picker.form.submit" defaultMessage="Apply" />
        </button>
      </div>
    </DatePicker>
  );
});

DatePickerInTimezone.displayName = 'DatePickerInTimezone';

export default DatePickerInTimezone;

DatePickerInTimezone.propTypes = {
  customCalendarClose: PropTypes.func.isRequired,
  finish: PropTypes.instanceOf(DateTime).isRequired,
  isModalOpen: PropTypes.bool,
  maxNumberDays: PropTypes.number.isRequired,
  minStart: PropTypes.number,
  onCalendarClose: PropTypes.func.isRequired,
  onCalendarOpen: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  start: PropTypes.instanceOf(DateTime).isRequired,
  timezone: PropTypes.string.isRequired,
};
DatePickerInTimezone.defaultProps = {
  isModalOpen: false,
  minStart: null,
};
